/* sp-cod-bridge.js — deep COD-form cart bridge for bundle tiers (Theme v1 contract).

   The standard buy-button contract (change events + ?variant= URL + [name=quantity]) can only express ONE
   variant × quantity. Mixed-variant bundle tiers and gift lines need the FULL selection mirrored into the COD
   app's Storefront-API cart via the app's window-level surface (`window._rsiV2` — getState/setState +
   cartService.{addProduct,removeProduct,getCart,applyDiscount}). The app builds its order FROM that cart, so
   correct lines in → correct order out.

   Behaviour learnt from live runs on Theme v1:
     - the app's own clearCart throws whenever cartLinesRemove reports userErrors, and our standard signals
       trigger the app's OWN in-flight cart rewrite at click time, so the bridge clears with the app's TOLERANT
       removeProduct instead (a stale id just means the line is already gone), bounded to two passes,
     - the bridge write defers BRIDGE_DELAY_MS past the click so the app's click-time cart work settles,
     - getCart resolves {data:{cart:{lines:{edges}}}}; applyDiscount takes an ARRAY of codes,
     - the app renders the form and builds the order body from its React state `cartInfo`, never re-reading
       the Storefront cart after an external write → setState({cartInfo}) after writing lines.

   Defensive by design — this asset must NEVER break the PDP or the button:
     - strict feature-detection: not a single call unless the surface shape matches exactly; any drift →
       console.warn + the standard contract stays in charge,
     - everything try/catch,
     - theme-editor kill-switch: section root `data-cod-bridge="false"` (section setting `cod_deep_sync`),
     - bounded retry: ONE delayed re-verify, ONE repair, then stop — no loops. */
(function () {
  'use strict';

  // The app injects its buy button dynamically (app embed), so a document-level CAPTURE-phase delegation is
  // the only reliable hook.
  var COD_BUTTON_SELECTOR =
    '#rsi_buy_now_button, ._rsi-buy-now-button, #floatingBuyNowButton, [href="/rsi-btn-hook"]';
  var SECTION_SELECTOR = '.sp-product[data-section]';
  var SELECTOR_ELEMENT = 'sp-bundle-form';
  var BRIDGE_DELAY_MS = 400;
  var REVERIFY_DELAY_MS = 300;

  function warn(detail) {
    console.warn('cod-bridge: app surface changed/failed, falling back to standard contract', detail);
  }

  // Kill-switch — a missing attribute counts as enabled.
  function killSwitchOff(el) {
    return !!(el && el.dataset && el.dataset.codBridge === 'false');
  }

  // Deep integration is only worth the exposure when the single-variant form contract cannot express the
  // selection. Single unit, no gifts, not mixed → the standard contract already carries the exact selection.
  function inScope(snapshot) {
    return snapshot.mainQuantity > 1 || snapshot.gifts.length > 0 || snapshot.mixed === true;
  }

  // Feature-detect the app surface — exact shape or nothing. getCart + removeProduct are needed for the
  // tolerant clear; setState is required too (a state-blind write changes nothing the customer sees).
  // applyDiscount stays optional at its point of use.
  function detectSurface() {
    var rsi = typeof window !== 'undefined' ? window._rsiV2 : null;
    if (!rsi || typeof rsi.getState !== 'function' || typeof rsi.setState !== 'function') {
      return null;
    }
    var cartService = rsi.cartService;
    if (
      !cartService ||
      typeof cartService.addProduct !== 'function' ||
      typeof cartService.removeProduct !== 'function' ||
      typeof cartService.getCart !== 'function'
    ) {
      return null;
    }
    return { rsi: rsi, cartService: cartService };
  }

  function toGid(variantId) {
    return 'gid://shopify/ProductVariant/' + variantId;
  }

  // Aggregate the snapshot into unique cart lines (same variant → one line with the summed quantity):
  // every unit counts 1, gifts carry their own qty.
  function buildLines(snapshot) {
    var order = [];
    var byId = {};
    function add(variantId, qty) {
      var key = String(variantId);
      if (!byId[key]) {
        byId[key] = { merchandiseId: toGid(variantId), quantity: 0 };
        order.push(key);
      }
      byId[key].quantity += qty;
    }
    snapshot.units.forEach(function (u) {
      add(u.variantId, 1);
    });
    snapshot.gifts.forEach(function (g) {
      add(g.variantId, g.quantity || 1);
    });
    return order.map(function (key) {
      return byId[key];
    });
  }

  // Unwrap the getCart return value into a plain array of line nodes. Anything unknown → null (callers warn,
  // never guess).
  function unwrapLines(cart) {
    if (!cart) return null;
    var lines =
      cart.lines ||
      (cart.cart && cart.cart.lines) ||
      (cart.data && cart.data.cart && cart.data.cart.lines) ||
      null;
    if (lines && Array.isArray(lines.edges)) {
      lines = lines.edges.map(function (edge) {
        return edge && edge.node;
      });
    }
    return Array.isArray(lines) ? lines : null;
  }

  // Tolerant {merchandiseId, quantity} extractor for re-verify.
  function extractLines(cart) {
    var lines = unwrapLines(cart);
    if (!lines) return null;
    var out = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (!line) return null;
      var merchandiseId = (line.merchandise && line.merchandise.id) || line.merchandiseId || null;
      var quantity = typeof line.quantity === 'number' ? line.quantity : Number(line.quantity);
      if (!merchandiseId || !isFinite(quantity)) return null;
      out.push({ merchandiseId: String(merchandiseId), quantity: quantity });
    }
    return out;
  }

  // Compare as id → quantity maps (order-insensitive).
  function sameLines(expected, actual) {
    if (expected.length !== actual.length) return false;
    var want = {};
    expected.forEach(function (l) {
      want[l.merchandiseId] = l.quantity;
    });
    for (var i = 0; i < actual.length; i++) {
      if (want[actual[i].merchandiseId] !== actual[i].quantity) return false;
    }
    return true;
  }

  // Current cart line ids for the tolerant clear. null → unknown shape.
  function readCartLineIds(cartService, cartId) {
    return Promise.resolve(cartService.getCart(cartId)).then(function (cart) {
      var lines = unwrapLines(cart);
      if (!lines) return null;
      var ids = [];
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        if (line && line.id) ids.push(String(line.id));
      }
      return { ids: ids, lines: lines };
    });
  }

  // Tolerant clear + add: read line ids → removeProduct (userErrors ignored) → ONE re-read → remove any
  // leftovers once more → addProduct. Two remove passes max, never a loop.
  function applyLines(cartService, cartId, lines) {
    return readCartLineIds(cartService, cartId).then(function (read) {
      if (read === null) {
        // Can't see the cart → can't clear safely; a blind add would stack onto the app's own line.
        return Promise.reject('getCart returned an unknown shape — bridge write skipped');
      }
      var cleared =
        read.ids.length === 0
          ? Promise.resolve()
          : Promise.resolve(cartService.removeProduct(cartId, read.ids))
              .then(function () {
                return readCartLineIds(cartService, cartId);
              })
              .then(function (second) {
                if (second && second.ids.length > 0) {
                  return cartService.removeProduct(cartId, second.ids);
                }
              });
      return cleared.then(function () {
        return cartService.addProduct(cartId, lines);
      });
    });
  }

  // Tolerant reader for the applyDiscount mutation result — only used to surface userErrors as a warn.
  function discountUserErrors(result) {
    if (!result) return null;
    var payload =
      (result.data && result.data.cartDiscountCodesUpdate) || result.cartDiscountCodesUpdate || null;
    if (payload && Array.isArray(payload.userErrors) && payload.userErrors.length > 0) {
      return payload.userErrors;
    }
    return null;
  }

  function applyDiscountOnce(cartService, cartId, snapshot) {
    if (!snapshot.discountCode) return;
    if (typeof cartService.applyDiscount !== 'function') {
      warn('applyDiscount missing on app surface — discount not mirrored');
      return;
    }
    // The app spreads its second argument — pass an ARRAY of codes, never a bare string. Discount failure
    // keeps the (already correct) lines — warn only. The result is returned so syncAppState can prefer its
    // (freshest) cart object.
    return Promise.resolve(cartService.applyDiscount(cartId, [snapshot.discountCode]))
      .then(function (result) {
        var errs = discountUserErrors(result);
        if (errs) warn(errs);
        return result;
      })
      .catch(function (err) {
        warn(err);
      });
  }

  // Tolerant cart-object extractor for mutation / getCart responses. A cart without lines is useless to the
  // app's products derivation → treated as not found.
  function unwrapCartObject(result) {
    if (!result) return null;
    var data = result.data || result;
    var cart =
      (data.cartDiscountCodesUpdate && data.cartDiscountCodesUpdate.cart) ||
      (data.cartLinesAdd && data.cartLinesAdd.cart) ||
      data.cart ||
      null;
    return cart && cart.lines ? cart : null;
  }

  // Sync the app's React state with the cart we just wrote. Cart object priority: discount-update response
  // (freshest) → cartLinesAdd response → a fresh getCart. Failure degrades with a warn.
  function syncAppState(rsi, cartService, cartId, addResult, discountResult) {
    var cart = unwrapCartObject(discountResult) || unwrapCartObject(addResult);
    var resolved = cart
      ? Promise.resolve(cart)
      : Promise.resolve(cartService.getCart(cartId)).then(unwrapCartObject);
    return resolved
      .then(function (cartInfo) {
        if (!cartInfo) {
          warn('no cart object to sync into app state — form may show stale lines');
          return;
        }
        return Promise.resolve(rsi.setState({ cartInfo: cartInfo })).catch(warn);
      })
      .catch(warn);
  }

  // ONE re-verify after a short delay (the app's own variant watcher may have raced our write), ONE repair,
  // then stop — a second mismatch only warns.
  function verifyOnce(surface, cartId, lines, attempt) {
    var cartService = surface.cartService;
    Promise.resolve(cartService.getCart(cartId))
      .then(function (cart) {
        var actual = extractLines(cart);
        if (actual === null) {
          warn('getCart returned an unknown shape — re-verify skipped');
          return;
        }
        if (sameLines(lines, actual)) return;
        if (attempt >= 1) {
          warn('cart lines still mismatched after one repair — leaving as-is');
          return;
        }
        return applyLines(cartService, cartId, lines).then(function (repairAddResult) {
          return syncAppState(surface.rsi, cartService, cartId, repairAddResult, null).then(function () {
            setTimeout(function () {
              verifyOnce(surface, cartId, lines, attempt + 1);
            }, REVERIFY_DELAY_MS);
          });
        });
      })
      .catch(warn);
  }

  function bridgeWrite(snapshot) {
    var surface = detectSurface();
    if (!surface) {
      warn('feature-detect: _rsiV2 surface shape mismatch');
      return;
    }
    // cartId is read AFTER the delay — the app's click-time work may have rebuilt its cart meanwhile.
    var state = surface.rsi.getState();
    var cartId = state && state.cartInfo && state.cartInfo.id;
    if (!cartId) {
      warn('no cartInfo.id in app state');
      return;
    }
    var lines = buildLines(snapshot);
    var addResult;
    applyLines(surface.cartService, cartId, lines)
      .then(function (result) {
        addResult = result;
        return applyDiscountOnce(surface.cartService, cartId, snapshot);
      })
      .then(function (discountResult) {
        return syncAppState(surface.rsi, surface.cartService, cartId, addResult, discountResult);
      })
      .then(function () {
        setTimeout(function () {
          verifyOnce(surface, cartId, lines, 0);
        }, REVERIFY_DELAY_MS);
      })
      .catch(warn);
  }

  function runBridge(selectorEl) {
    var snapshot =
      typeof selectorEl.getSelectionSnapshot === 'function' ? selectorEl.getSelectionSnapshot() : null;
    if (!snapshot) return;
    if (!inScope(snapshot)) return;

    // The snapshot is frozen at click time (what the customer saw), but the write waits out the app's own
    // click-time cart work — writing immediately races its in-flight rewrite.
    setTimeout(function () {
      try {
        bridgeWrite(snapshot);
      } catch (err) {
        warn(err);
      }
    }, BRIDGE_DELAY_MS);
  }

  function onCodClick(event) {
    try {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest(COD_BUTTON_SELECTOR)) return;
      // The section the button lives in (the app injects near the form), else the first product section.
      var section = target.closest(SECTION_SELECTOR) || document.querySelector(SECTION_SELECTOR);
      if (!section || killSwitchOff(section)) return;
      var selectorEl = section.querySelector(SELECTOR_ELEMENT);
      if (!selectorEl) return;
      runBridge(selectorEl);
    } catch (err) {
      warn(err);
    }
  }

  document.addEventListener('click', onCodClick, true);
})();
